import type { Control, FieldValues, Path } from "react-hook-form";
import { FormCheckboxField } from "@/components/base/form/form-checkbox-field";
import { FormCurrencyField } from "@/components/base/form/form-currency-field";
import { FormPercentageField } from "@/components/base/form/form-percentage-field";
import { FormSelectField } from "@/components/base/form/form-select-field";
import { FormTextField } from "@/components/base/form/form-text-field";
import { FormField } from "@/components/base/form/hook-form";
import { SelectItem } from "@/components/base/select/select-item";
import { PayRateType } from "@fp/admin-api-client/types/payRateType";
import { cx } from "@/utils/cx";
import { z } from "zod";

export interface RateFieldNames {
	rateType: string;
	rate: string;
	useNumberOfUnits: string;
	numberOfUnits: string;
	useUnitsPerPerson: string;
	unitsPerPerson: string;
}

export interface RateSectionProps<T extends FieldValues> {
	control: Control<T>;
	fieldNames: RateFieldNames;
	rateType: PayRateType;
	useNumberOfUnits: boolean;
	useUnitsPerPerson: boolean;
}

export const RateSection = <T extends FieldValues>({
	control,
	fieldNames,
	rateType,
	useNumberOfUnits,
	useUnitsPerPerson,
}: RateSectionProps<T>) => {
	return (
		<div className="space-y-4">
			<FormField
				control={control}
				name={fieldNames.rateType as Path<T>}
				render={({ field }) => (
					<FormSelectField
						field={field}
						label="Rate Type"
						items={[
							{ id: PayRateType.FlatRate, label: "Flat Rate" },
							{ id: PayRateType.Hourly, label: "Per Hour" },
							{ id: PayRateType.PerUnit, label: "Per Unit" },
							{ id: PayRateType.Percentage, label: "Percentage" },
						]}
					>
						{(item) => <SelectItem {...item}>{item.label}</SelectItem>}
					</FormSelectField>
				)}
			/>
			{(rateType === PayRateType.FlatRate ||
				rateType === PayRateType.Hourly) && (
				<FormField
					control={control}
					name={fieldNames.rate as Path<T>}
					render={({ field }) => (
						<FormCurrencyField field={field} label="Pay Rate" placeholder="0" />
					)}
				/>
			)}
			{rateType === PayRateType.PerUnit && (
				<>
					<FormField
						control={control}
						name={fieldNames.rate as Path<T>}
						render={({ field }) => (
							<FormCurrencyField
								field={field}
								label="Pay Rate per Unit"
								placeholder="0"
							/>
						)}
					/>
					<div className="flex justify-between items-center">
						<FormField
							control={control}
							name={fieldNames.useNumberOfUnits as Path<T>}
							render={({ field }) => (
								<FormCheckboxField field={field} label="Number of Units" />
							)}
						/>
						<div
							className={cx(
								!useNumberOfUnits && "opacity-0 pointer-events-none",
							)}
						>
							<FormField
								control={control}
								name={fieldNames.numberOfUnits as Path<T>}
								render={({ field }) => (
									<FormTextField
										field={field}
										aria-label="Number of units value"
									/>
								)}
							/>
						</div>
					</div>
					<div className="flex justify-between items-center">
						<FormField
							control={control}
							name={fieldNames.useUnitsPerPerson as Path<T>}
							render={({ field }) => (
								<FormCheckboxField
									field={field}
									label="Units per Participant"
								/>
							)}
						/>
						<div
							className={cx(
								!useUnitsPerPerson && "opacity-0 pointer-events-none",
							)}
						>
							<FormField
								control={control}
								name={fieldNames.unitsPerPerson as Path<T>}
								render={({ field }) => (
									<FormTextField
										field={field}
										aria-label="Units per participant value"
									/>
								)}
							/>
						</div>
					</div>
				</>
			)}
			{rateType === PayRateType.Percentage && (
				<FormField
					control={control}
					name={fieldNames.rate as Path<T>}
					render={({ field }) => (
						<FormPercentageField field={field} label="Rate" placeholder="0" />
					)}
				/>
			)}
		</div>
	);
};

export const rateSchemaFields = {
	rateType: z.enum([
		PayRateType.Hourly,
		PayRateType.FlatRate,
		PayRateType.PerUnit,
		PayRateType.Percentage,
	]),
	rate: z.string().min(1, { error: "Rate is required" }),
	useNumberOfUnits: z.boolean().optional(),
	numberOfUnits: z.string().optional(),
	useUnitsPerPerson: z.boolean().optional(),
	unitsPerPerson: z.string().optional(),
};

export const rateRefinement = (values: { useNumberOfUnits?: boolean; numberOfUnits?: string }) => {
	if (!values.useNumberOfUnits) return true;
	return values.numberOfUnits !== undefined && values.numberOfUnits !== "";
};

export const rateRefinementMessage = "Number of Units is required";
